import React, { useState, useEffect } from 'react';

import mainStyle from "../../dashboard.module.scss";
import { styled, useTheme } from '@mui/material/styles';
import { Box, Grid, Typography, Tooltip } from '@mui/material';

import icn_grid_chart from 'assets/images/icons/icn_grid_chart.svg';

const TWO_POINT_FOUR_GHZ_RANGE = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14]
const FIVE_GHZ_RANGE = [36, 40, 44, 48, 52, 56, 60, 64, 100, 104, 108, 112, 124, 128, 132, 136, 140, 144, 149, 153, 157, 161, 165]
const SIX_GHZ_RANGE = [1, 5, 9, 13, 17, 21, 25, 29, 33, 37, 41, 45, 49, 53, 57, 61, 65, 69, 73, 77, 81, 85, 89, 93, 97, 101, 105, 109, 113,
    117, 121, 125, 129, 133, 137, 141, 145, 149, 153, 157, 161, 165, 169, 173, 177, 181, 185, 189, 193, 197, 201, 205, 209, 213, 217, 221, 225, 229, 233]

const ChannelBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'flex-end',
    paddingBottom: '6px',
    overflowX: 'auto',
    overflowY: 'hidden',
    marginTop: '5px',

    "&::-webkit-scrollbar": {
        height: 5,
    },
    "&::-webkit-scrollbar-thumb": {
        backgroundColor: theme.palette.primary.main,
        borderRadius: 8,
    },
    "&::-webkit-scrollbar-thumb:hover": {
        backgroundColor: theme.palette.primary.mainHover,
        cursor: "ew-resize",
    },
    "&::-webkit-scrollbar-track": {
        backgroundColor: theme.palette.primary.greyThread,
        borderRadius: theme.shape.customBorderRadius[0],
    },
    "&::-webkit-scrollbar-button": {
        display: "none",
        width: 0,
        height: 0,
    },
}));

const ScrollTooltipBox = styled(Box)(({ theme }) => ({
    maxHeight: '110px',
    overflowY: 'auto',
    paddingRight: '4px',

    "&::-webkit-scrollbar": {
        width: 5,
    },
    "&::-webkit-scrollbar-thumb": {
        backgroundColor: theme.palette.primary.main,
        borderRadius: 8,
    },
    "&::-webkit-scrollbar-thumb:hover": {
        backgroundColor: theme.palette.primary.mainHover,
        cursor: "ew-resize",
    },
    "&::-webkit-scrollbar-track": {
        backgroundColor: theme.palette.primary.greyThread,
        borderRadius: theme.shape.customBorderRadius[0],
    },
    "&::-webkit-scrollbar-button": {
        display: "none",
        width: 0,
        height: 0,
    },
}));

const PortField = ({ portStyle, label }) => {
    const theme = useTheme();
    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: 20,
                maxWidth: 30,
                height: 20,
                borderRadius: '2px',
                px: 1,
                backgroundColor: label
                    ? portStyle.selectBackgroundColor
                    : portStyle.backgroundColor,
                color: theme.palette.primary.white,
                fontSize: theme.typography.customFontSize[0],
                lineHeight: 1.33,
                mx: 0.5,
                whiteSpace: 'nowrap',
                cursor: 'default'
            }}
        >
            {label}
        </Box>
    )
}

const ChannelField = ({ channelTitle, channelData, portData }) => {
    const theme = useTheme();

    const portStyleSheet = {
        "2.4GHz": {
            backgroundColor: theme.palette.primary.orangeSecound,
            selectBackgroundColor: theme.palette.primary.orange,
        },
        "5GHz": {
            backgroundColor: theme.palette.primary.greenFourth,
            selectBackgroundColor: theme.palette.primary.greenPrimary,
        },
        "6GHz": {
            backgroundColor: theme.palette.primary.lightBlue,
            selectBackgroundColor: theme.palette.primary.main,
        }
    }

    return (
        <Box sx={{ marginLeft: '22px' }}>
            <Typography
                sx={{
                    color: theme.palette.primary.greyPrimary,
                    fontSize: theme.typography.customFontSize[3],
                    fontWeight: theme.typography.customFontWeight[1],
                    lineHeight: 0.95,
                    padding: '3px'
                }}
            >
                {channelTitle}
            </Typography>
            <ChannelBox>
                {channelData.map((channelNumber) => {
                    const channelInfo = portData.find((item) => item.channel === channelNumber);
                    return (
                        <Tooltip
                            key={Math.random()}
                            arrow
                            title={
                                channelInfo ? (
                                    <ScrollTooltipBox>
                                        {channelInfo.devices.map((item) => (
                                            <Typography key={item.id} sx={{
                                                fontSize: theme.typography.customFontSize[1],
                                                fontWeight: theme.typography.customFontWeight[1],
                                                lineHeight: 1.69,
                                                color: theme.palette.primary.white,
                                                whiteSpace: 'nowrap'
                                            }}>
                                                {item.name || item.id}
                                            </Typography>
                                        ))}
                                    </ScrollTooltipBox>
                                ) : ""}
                        >
                            <Box key={`${channelTitle}-${channelNumber}`} sx={{ textAlign: "center", mx: 0.5, flex: '0 0 auto', marginTop: '5px' }}>
                                <Typography
                                    sx={{
                                        fontSize: theme.typography.customFontSize[0],
                                        fontWeight: theme.typography.customFontWeight[0],
                                        lineHeight: 1.33,
                                        color: theme.palette.primary.greySixth,
                                        mb: 0.5,
                                        cursor: 'default'
                                    }}
                                >
                                    {channelNumber}
                                </Typography>
                                <PortField
                                    portStyle={portStyleSheet[channelTitle]}
                                    label={channelInfo && channelInfo.total}
                                />
                            </Box>
                        </Tooltip>
                    );
                })}
            </ChannelBox>
        </Box>
    )
}

const ApChannelUtilization = ({ sortable, data }) => {
    const [twoPointFourGHzChannelData, setTwoPointFourGHzChannelData] = useState([]);
    const [fiveGHzChannelData, setFiveGHzChannelData] = useState([]);
    const [sixGHzChannelData, setSixGHzChannelData] = useState([]);

    useEffect(() => {
        const allBands = ["2.4GHz", "5GHz", "6GHz"];
        const map = new Map(allBands.map(item => [item, []]));

        data.forEach(({ band, channel, total, devices }) => {
            if (map.has(band)) map.get(band).push({ channel, total, devices });
        });

        setTwoPointFourGHzChannelData(map.get("2.4GHz"));
        setFiveGHzChannelData(map.get("5GHz"));
        setSixGHzChannelData(map.get("6GHz"));
    }, [data]);

    return (
        <div
            style={{ height: "250px" }}
            className={`main-card-content ${mainStyle["dndObj_card"]} ${sortable === true ? mainStyle["dndObj_edit"] : ""}`}
        >
            <div className={mainStyle['dndObj_title']}>
                <Box>
                    <img src={icn_grid_chart} alt="icn_grid_chart" />
                </Box>
                <span style={{ marginLeft: '10px' }}>Access Points Channel Utilization</span>
            </div>
            <Grid container style={{ marginTop: '10px' }}>
                <Grid item xs={4} style={{ marginBottom: '15px' }}>
                    <ChannelField
                        channelTitle="2.4GHz"
                        channelData={TWO_POINT_FOUR_GHZ_RANGE}
                        portData={twoPointFourGHzChannelData}
                    />
                </Grid>
                <Grid item xs={8} style={{ marginBottom: '15px' }}>
                    <ChannelField
                        channelTitle="5GHz"
                        channelData={FIVE_GHZ_RANGE}
                        portData={fiveGHzChannelData}
                    />
                </Grid>
                <Grid item xs={12}>
                    <ChannelField
                        channelTitle="6GHz"
                        channelData={SIX_GHZ_RANGE}
                        portData={sixGHzChannelData}
                    />
                </Grid>
            </Grid>
        </div>
    );
};

export default ApChannelUtilization;